import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Box,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, GroupAdd, CalendarMonth as CalendarIcon, Lock as LockIcon } from '@mui/icons-material';
import { apiRequest } from '../api/api';
import { useSettings } from '../context/SettingsContext';
import { useNavigate } from 'react-router-dom';

// Colore della pagina Gestione Quantità Personale
const PAGE_COLOR = '#b052c0';

// Componente per input numerico con layout - numero +
interface QuantityInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}

const QuantityInput: React.FC<QuantityInputProps> = ({ value, onChange, min = 0, max = 20 }) => {
  const handleIncrement = () => {
    if (value < max) {
      onChange(value + 1);
    }
  };

  const handleDecrement = () => {
    if (value > min) {
      onChange(value - 1);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value) || 0;
    if (newValue >= min && newValue <= max) {
      onChange(newValue);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
      <IconButton
        size="small"
        onClick={handleDecrement}
        disabled={value <= min}
        sx={{ 
          width: 24, 
          height: 24,
          bgcolor: 'primary.main',
          color: 'white',
          '&:hover': {
            bgcolor: 'primary.dark',
          },
          '&:disabled': {
            bgcolor: 'grey.300',
            color: 'grey.500',
          }
        }}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      <TextField
        size="small"
        value={value}
        onChange={handleInputChange}
        inputProps={{
          style: { textAlign: 'center', padding: '2px' },
          min,
          max
        }}
        sx={{ 
          width: 50,
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
          '& input[type=number]::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '& input[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        }}
        type="number"
      />
      <IconButton
        size="small"
        onClick={handleIncrement}
        disabled={value >= max}
        sx={{ 
          width: 24, 
          height: 24,
          bgcolor: 'primary.main',
          color: 'white',
          '&:hover': {
            bgcolor: 'primary.dark',
          },
          '&:disabled': {
            bgcolor: 'grey.300',
            color: 'grey.500',
          }
        }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

interface QuantitaPersonale {
  id: number;
  giornoSettimana: string;
  mese: string;
  neg: string;
  intermezzo: number;
  mattina: number;
  pomeriggio: number;
}

interface QuantitaPersonaleRow {
  giorno: string;
  mattina: number;
  pomeriggio: number;
  intermezzo: number;
  record?: QuantitaPersonale;
}

interface Negozio {
  neg: string;
  negozio: string;
}

interface DipendenteBase {
  codicePersonale: string;
  nome: string;
}

interface GaranziaMinimaTurni {
  id?: number;
  negozio: string;
  codicePersonale: string;
  giornoSettimana: number; // 1=Lunedì...7=Domenica
  mese?: string;  // es. "Giugno"
  anno?: number;  // es. 2026
  fascia: 'M' | 'P';
  quantitaMinima: number;
}

// key: `${codicePersonale}_${giornoSettimana}_${fascia}`
type GaranzieMap = Record<string, GaranziaMinimaTurni>;

// Quadratino cliccabile per le garanzie (cicla 0→1→2→3→0)
// Colori allineati a generazione orari:
//   M=Mattina #35abec (testo bianco)
//   P=Pomeriggio #FDD65C (testo nero)
//   I=Intermezzo #60b964 (testo bianco)
const FASCIA_COLORS: Record<string, { bg: string; text: string }> = {
  M: { bg: '#35abec', text: '#fff' },
  P: { bg: '#FDD65C', text: '#333' },
};

interface GaranziaSquareProps {
  label: string;
  value: number;
  dayRemaining: number; // spazio residuo nel giorno (escluso il valore corrente)
  onChange: (v: number) => void;
  readOnly?: boolean;
}

const GaranziaSquare: React.FC<GaranziaSquareProps> = ({ label, value, dayRemaining, onChange, readOnly }) => {
  const handleClick = () => {
    if (readOnly) return;
    const next = (value + 1) % 4;
    // Se aumentare supererebbe il limite giornaliero di 3, torna a 0
    if (next > 0 && next > value + dayRemaining) {
      onChange(0);
    } else {
      onChange(next);
    }
  };
  const palette = FASCIA_COLORS[label] ?? { bg: '#b052c0', text: '#fff' };
  return (
    <Box
      onClick={handleClick}
      title={`${label}: ${value} — click per cambiare`}
      sx={{
        width: 30,
        height: 30,
        border: `1.5px solid ${value > 0 ? palette.bg : '#ccc'}`,
        borderRadius: '4px',
        cursor: readOnly ? 'default' : 'pointer',
        bgcolor: value > 0 ? palette.bg : '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        transition: 'all 0.15s ease',
        '&:hover': readOnly ? {} : { opacity: 0.8, transform: 'scale(1.08)' },
      }}
    >
      <Typography sx={{ fontSize: '0.55rem', lineHeight: 1, color: value > 0 ? palette.text : '#999', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: value > 0 ? palette.text : '#bbb', lineHeight: 1 }}>
        {value > 0 ? value : '·'}
      </Typography>
    </Box>
  );
};

const QuantitaPersonaleList: React.FC = () => {
  const { settings, setSettings } = useSettings();
  const navigate = useNavigate();

  // Solo Admin e Coordinatore possono modificare le garanzie
  const canEditGaranzie = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const ruolo = String(u?.ruolo || '').toLowerCase();
      return ruolo === 'admin';
    } catch { return false; }
  })();

  const [quantitaData, setQuantitaData] = useState<QuantitaPersonale[]>([]);
  const [negoziAccessibili, setNegoziAccessibili] = useState<Negozio[]>([]);
  const [selectedNegozio, setSelectedNegozio] = useState<string>('');
  const [selectedMese, setSelectedMese] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<QuantitaPersonaleRow[]>([]);

  // Garanzie turni
  const [dipendentiGaranzie, setDipendentiGaranzie] = useState<DipendenteBase[]>([]);
  const [garanzieMap, setGaranzieMap] = useState<GaranzieMap>({});
  const [loadingGaranzie, setLoadingGaranzie] = useState(false);

  const giorni = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];
  const giorniItaliani = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];
  const mesi = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  useEffect(() => {
    fetchNegoziAccessibili();
  }, []);

  // Applica preferenze salvate quando disponibili
  useEffect(() => {
    if (settings?.storeId && negoziAccessibili.length > 0) {
      setSelectedNegozio(settings.storeId);
    }
  }, [settings?.storeId, negoziAccessibili.length]);

  useEffect(() => {
    if (settings?.month) {
      const monthNames = mesi;
      const name = monthNames[settings.month - 1];
      if (name) setSelectedMese(name);
    }
  }, [settings?.month]);

  useEffect(() => {
    if (selectedNegozio && selectedMese) {
      fetchQuantitaPersonale();
    }
  }, [selectedNegozio, selectedMese]);

  useEffect(() => {
    if (quantitaData.length > 0) {
      createRows();
    }
  }, [quantitaData]);

  // Fetch dipendenti + garanzie quando cambia il negozio/mese
  useEffect(() => {
    if (!selectedNegozio || !selectedMese) return;
    const anno = settings?.year || new Date().getFullYear();
    const loadGaranzie = async () => {
      setLoadingGaranzie(true);
      try {
        const [dipList, garList] = await Promise.all([
          apiRequest<any[]>(`/dipendenti/negozio/${selectedNegozio}`),
          apiRequest<GaranziaMinimaTurni[]>(
            `/garanzie-turni/negozio/${selectedNegozio}?mese=${encodeURIComponent(selectedMese)}&anno=${anno}`
          )
        ]);
        setDipendentiGaranzie(
          (dipList || []).map((d: any) => ({ codicePersonale: d.codicePersonale, nome: d.nome }))
        );
        const map: GaranzieMap = {};
        (garList || []).forEach((g) => {
          map[`${g.codicePersonale}_${g.giornoSettimana}_${g.fascia}`] = g;
        });
        setGaranzieMap(map);
      } catch (err) {
        console.error('Errore nel caricamento garanzie turni:', err);
      } finally {
        setLoadingGaranzie(false);
      }
    };
    loadGaranzie();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNegozio, selectedMese, settings?.year]);

  const handleGaranziaChange = async (
    codicePersonale: string,
    giornoSettimana: number,
    fascia: 'M' | 'P',
    quantita: number
  ) => {
    const anno = settings?.year || new Date().getFullYear();
    const key = `${codicePersonale}_${giornoSettimana}_${fascia}`;
    const existing = garanzieMap[key];
    const payload: GaranziaMinimaTurni = {
      ...(existing || {}),
      negozio: selectedNegozio,
      codicePersonale,
      giornoSettimana,
      mese: selectedMese,
      anno,
      fascia,
      quantitaMinima: quantita,
    };
    // Aggiornamento ottimistico
    setGaranzieMap(prev => ({ ...prev, [key]: payload }));
    try {
      const saved = await apiRequest<GaranziaMinimaTurni>('/garanzie-turni', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setGaranzieMap(prev => ({ ...prev, [key]: saved }));
    } catch (err) {
      console.error('Errore nel salvataggio garanzia:', err);
      // ripristina
      setGaranzieMap(prev => ({ ...prev, [key]: existing || payload }));
    }
  };

  const fetchNegoziAccessibili = async () => {
    try {
      const data = await apiRequest<Negozio[]>('/negozi/accessibili');
      setNegoziAccessibili(data);
      if (data.length > 0 && !settings?.storeId) {
        setSelectedNegozio(data[0].neg);
      }
      if (mesi.length > 0) {
        // Se non c'è preferenza salvata, default al primo mese
        if (!settings?.month) setSelectedMese(mesi[0]);
      }
    } catch (err) {
      setError('Errore nel caricamento dei negozi accessibili');
      console.error('Error fetching negozi accessibili:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuantitaPersonale = async () => {
    if (!selectedNegozio || !selectedMese) return;
    
    try {
      setLoading(true);
      const data = await apiRequest<QuantitaPersonale[]>(
        `/quantita-personale/negozio/${selectedNegozio}/mese/${selectedMese}`
      );
      setQuantitaData(data);
      setError(null);
    } catch (err: any) {
      if (err.status === 403) {
        setError('Non hai i permessi per visualizzare questi dati');
      } else {
        setError('Errore nel caricamento dei dati');
      }
      console.error('Error fetching quantita personale:', err);
    } finally {
      setLoading(false);
    }
  };

  const createRows = () => {
    const newRows: QuantitaPersonaleRow[] = giorni.map((giorno, index) => {
      // Trova il record per questo giorno (dovrebbe essercene solo uno per giorno/negozio/mese)
      const dayRecord = quantitaData.find(q => q.giornoSettimana === giorno);
      
      return {
        giorno: giorniItaliani[index],
        mattina: dayRecord?.mattina || 0,
        pomeriggio: dayRecord?.pomeriggio || 0,
        intermezzo: dayRecord?.intermezzo || 0,
        record: dayRecord
      };
    });
    
    setRows(newRows);
  };

  const handleQuantitaChange = async (
    giornoIndex: number,
    turno: 'mattina' | 'pomeriggio' | 'intermezzo',
    value: number
  ) => {
    const newRows = [...rows];
    newRows[giornoIndex][turno] = value;
    setRows(newRows);

    // Trova o crea il record corrispondente
    const giorno = giorni[giornoIndex];
    const existingRecord = newRows[giornoIndex].record;
    
    try {
      if (existingRecord) {
        // Aggiorna record esistente
        const updatedRecord = { 
          ...existingRecord, 
          [turno]: value,
          giornoSettimana: giorno,
          mese: selectedMese,
          neg: selectedNegozio
        };
        await apiRequest(`/quantita-personale/${existingRecord.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedRecord),
        });
        
        // Aggiorna il record nella riga
        newRows[giornoIndex].record = updatedRecord;
        setRows(newRows);
      } else {
        // Crea nuovo record
        const newRecord = {
          giornoSettimana: giorno,
          mese: selectedMese,
          neg: selectedNegozio,
          mattina: turno === 'mattina' ? value : 0,
          pomeriggio: turno === 'pomeriggio' ? value : 0,
          intermezzo: turno === 'intermezzo' ? value : 0
        };
        
        const createdRecord = await apiRequest<QuantitaPersonale>('/quantita-personale', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newRecord),
        });
        
        // Aggiorna il record nella riga
        newRows[giornoIndex].record = createdRecord;
        setRows(newRows);
      }
    } catch (err) {
      setError('Errore nel salvare i dati');
      console.error('Error saving quantita personale:', err);
      // Ripristina il valore precedente in caso di errore
      fetchQuantitaPersonale();
    }
  };

  const handleSalvaTutto = async () => {
    try {
      setLoading(true);
      await fetchQuantitaPersonale(); // Ricarica per confermare i salvataggi
      setError(null);
    } catch (err) {
      setError('Errore nel salvare tutti i dati');
      console.error('Error saving all data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Persiste le selezioni quando cambiano
  useEffect(() => {
    if (selectedNegozio) {
      const monthIndex = mesi.indexOf(selectedMese);
      if (monthIndex >= 0) {
        const monthNumber = monthIndex + 1;
        setSettings(prev => ({
          ...(prev || { storeId: selectedNegozio, month: monthNumber, year: new Date().getFullYear() }),
          storeId: selectedNegozio,
          month: monthNumber,
          year: (settings?.year || new Date().getFullYear())
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNegozio, selectedMese]);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Typography>Caricamento...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          <GroupAdd fontSize="large" sx={{ mr: 2, color: '#b052c0' }} />
          <Typography variant="h4" component="h1">
            Gestione Quantità Personale
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Seleziona Negozio</InputLabel>
            <Select
              value={selectedNegozio}
              label="Seleziona Negozio"
              onChange={(e) => setSelectedNegozio(e.target.value)}
            >
              {negoziAccessibili.map((negozio) => (
                <MenuItem key={negozio.neg} value={negozio.neg}>
                  {negozio.neg} - {negozio.negozio}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Seleziona Mese</InputLabel>
            <Select
              value={selectedMese}
              label="Seleziona Mese"
              onChange={(e) => setSelectedMese(e.target.value)}
            >
              {mesi.map((mese) => (
                <MenuItem key={mese} value={mese}>
                  {mese}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleSalvaTutto}
            disabled={loading}
            sx={{
              bgcolor: PAGE_COLOR,
              color: 'white',
              '&:hover': {
                bgcolor: '#8E24AA', // Versione più scura di #b052c0
              },
              '&:disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500',
              }
            }}
          >
            SALVA TUTTO
          </Button>

          <Box sx={{ ml: 'auto' }} />

          <Button
            variant="contained"
            startIcon={<CalendarIcon />}
            onClick={() => {
              // Assicura che le preferenze siano allineate prima di navigare
              const monthIndex = mesi.indexOf(selectedMese);
              const monthNumber = monthIndex >= 0 ? monthIndex + 1 : (settings?.month || new Date().getMonth() + 1);
              const year = settings?.year || new Date().getFullYear();
              if (selectedNegozio && monthNumber) {
                setSettings(prev => ({
                  ...(prev || {}),
                  storeId: selectedNegozio,
                  month: monthNumber,
                  year,
                }));
              }
              navigate('/generazione-orario');
            }}
            disabled={!selectedNegozio || !selectedMese}
            sx={{
              height: '56px',
              bgcolor: '#607D8B',
              color: 'white',
              '&:hover': {
                bgcolor: '#607D8B',
              },
              '&:disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500',
              }
            }}
          >
            Vai a Generazione Orari
          </Button>
        </Box>

        {selectedNegozio && selectedMese && (
          <Typography variant="h6" sx={{ mb: 2 }}>
            Negozio: {selectedNegozio} - Mese: {selectedMese}
          </Typography>
        )}

        {rows.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(156, 39, 176, 0.1)' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Giorno</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Mattina</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pomeriggio</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Intermezzo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.giorno}>
                    <TableCell component="th" scope="row">
                      <strong>{row.giorno}</strong>
                    </TableCell>
                    <TableCell align="center">
                      <QuantityInput
                        value={row.mattina}
                        onChange={(value) => handleQuantitaChange(index, 'mattina', value)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <QuantityInput
                        value={row.pomeriggio}
                        onChange={(value) => handleQuantitaChange(index, 'pomeriggio', value)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <QuantityInput
                        value={row.intermezzo}
                        onChange={(value) => handleQuantitaChange(index, 'intermezzo', value)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ========== GARANZIE TURNI ========== */}
      {selectedNegozio && canEditGaranzie && (
        <Box sx={{ mt: 4, mb: 8, px: 1 }}>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <LockIcon sx={{ color: '#b052c0' }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              Garanzie turni
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Definisci il numero minimo di turni garantiti per dipendente per ogni giorno e fascia.
            Questi valori sono <strong>vincoli hard</strong> nel motore di generazione: vengono soddisfatti
            con priorità assoluta. In caso di impossibilità, il sistema lo segnala prima della conferma del piano.
          </Typography>

          {loadingGaranzie ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : dipendentiGaranzie.length === 0 ? (
            <Alert severity="info">Nessun dipendente trovato per il negozio selezionato.</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(176, 82, 192, 0.1)' }}>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 140, position: 'sticky', left: 0, backgroundColor: 'rgba(176, 82, 192, 0.1)', zIndex: 1 }}>
                      Dipendente
                    </TableCell>
                    {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((label, i) => (
                      <TableCell key={i + 1} align="center" sx={{ fontWeight: 'bold', minWidth: 72, px: 0.5 }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dipendentiGaranzie.map((dip) => (
                    <TableRow key={dip.codicePersonale} hover>
                      <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
                        <Tooltip title={dip.codicePersonale} placement="right">
                          <span>{dip.nome}</span>
                        </Tooltip>
                      </TableCell>
                      {[1, 2, 3, 4, 5, 6, 7].map((dow) => {
                        const dayTotal = (['M', 'P'] as const).reduce(
                          (sum, f) => sum + (garanzieMap[`${dip.codicePersonale}_${dow}_${f}`]?.quantitaMinima ?? 0),
                          0
                        );
                        return (
                          <TableCell key={dow} align="center" sx={{ py: 0.5, px: 0.5 }}>
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              {(['M', 'P'] as const).map((fascia) => {
                                const key = `${dip.codicePersonale}_${dow}_${fascia}`;
                                const val = garanzieMap[key]?.quantitaMinima ?? 0;
                                return (
                                  <GaranziaSquare
                                    key={fascia}
                                    label={fascia}
                                    value={val}
                                    dayRemaining={3 - dayTotal}
                                    onChange={(v) => handleGaranziaChange(dip.codicePersonale, dow, fascia, v)}
                                    readOnly={!canEditGaranzie}
                                  />
                                );
                              })}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Container>
  );
};

export default QuantitaPersonaleList;
